import React from "react";
import { IDialogProps } from "./interfaces/IDialogProps";
import {
  Dialog as MuiDialog,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Stack,
  IconButton,
  Theme,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";

export const Dialog: React.FC<IDialogProps> = (props) => {
  const {
    open,
    title,
    subtitle,
    size,
    variant = "default",
    headerIcon,
    fullScreen = false,
    hideCloseIcon = false,
    contentWrappedWithForm,
    contentSx,
    titleSx,
    actions,
    customActions,
    onClose,
    rightHeading,
    paperSx,
  } = props;

  const isError = variant === "error";

  const generateActions = () => {
    // A fully custom footer node takes precedence over the button-config array.
    if (customActions) {
      return (
        <DialogActions
          sx={{
            px: 3,
            py: 1.5,
            bgcolor: (t: Theme) => t.palette.action.hover,
            borderTop: (t: Theme) => `1px solid ${t.palette.divider}`,
          }}
        >
          {customActions}
        </DialogActions>
      );
    }
    if (!actions) return null;
    return (
      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          bgcolor: (t: Theme) => t.palette.action.hover,
          borderTop: (t: Theme) => `1px solid ${t.palette.divider}`,
        }}
      >
        {actions.map((action, index) => (
          <Button
            key={`dialog-action-${index}-${action.label}`}
            variant={action.variant}
            color={action.color ? action.color : isError ? "error" : "primary"}
            type={action.type}
            disabled={action.disabled}
            onClick={action.onClick}
            disableElevation
            sx={{
              fontWeight: 700,
              px: 3,
              py: 1,
              borderRadius: 2,
              ml: 1,
              ...(action.sx || {}),
            }}
            startIcon={action.startIcon}
          >
            {action.label}
          </Button>
        ))}
      </DialogActions>
    );
  };

  const renderBody = () => {
    const actionsButtons = generateActions();
    if (contentWrappedWithForm) {
      return (
        <form
          onSubmit={(e) => {
            e.stopPropagation();
            contentWrappedWithForm.onSubmit(e);
          }}
        >
          <DialogContent sx={{ overflowY: "auto", maxHeight: "70vh", ...contentSx }}>
            {props.children}
          </DialogContent>
          {actionsButtons}
        </form>
      );
    }
    return (
      <>
        <DialogContent sx={contentSx}>{props.children}</DialogContent>
        {actionsButtons}
      </>
    );
  };

  return (
    <MuiDialog
      open={open}
      fullScreen={fullScreen}
      maxWidth={size}
      fullWidth
      TransitionComponent={fullScreen ? Transition : undefined}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: "hidden",
          boxShadow: "0 12px 40px rgba(0,0,0,0.16)",
          ...paperSx,
        },
      }}
    >
      {/* ---------- Header ---------- */}
      <Box
        sx={(theme: Theme) => {
          const { typography, ...restTitleSx } = titleSx || {};
          return {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            bgcolor: isError
              ? theme.palette.error.main
              : theme.palette.primary.main,
            color: "#fff",
            flexShrink: 0,
            ...restTitleSx,
          };
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          {headerIcon && (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255,255,255,0.15)",
                color: "#fff",
                "& svg": { fontSize: 22, color: "#fff" },
              }}
            >
              {headerIcon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ color: "#fff", lineHeight: 1.2, ...(titleSx?.typography || {}) }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.75)", mt: 0.25 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {rightHeading && <Box>{rightHeading}</Box>}
          {!hideCloseIcon && (
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Close dialog"
              sx={{
                border: "1px solid rgba(255,255,255,0.35)",
                borderRadius: 2,
                color: "#fff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Box>

      {renderBody()}
    </MuiDialog>
  );
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});
